import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const pill = (s) => ({ pending: "bg-amber-100 text-amber-800", verified: "bg-green-100 text-green-800", approved: "bg-blue-100 text-blue-800", disbursed: "bg-green-100 text-green-800" }[s] || "bg-red-50 text-red-800");
const Th = ({ cols }) => <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3">{c}</th>)}</tr></thead>;
const Btn = ({ className = "bg-[#2F7D32] hover:bg-[#1B5E20]", ...p }) => <Button {...p} className={`h-8 text-xs rounded-full px-3 ${className}`} />;

async function openPrivate(path) {
  const r = await api.get(`/admin/private-files/${path}`, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
}

export function NgosAdminTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/ngos").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const set = async (id, status) => {
    const note = status === "rejected" ? window.prompt("Reason (shown to the NGO)") : null;
    try { await api.put(`/admin/ngos/${id}/status`, { status, note }); toast.success(status); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="ngos-admin-tab" className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]">
        <Th cols={["NGO", "Registration", "PAN", "Subscription", "Status", "Actions"]} />
        <tbody>
          {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#63534E]">No NGO applications yet.</td></tr>}
          {items.map((n) => (
            <tr key={n.id} className="border-t border-[#D4AF37]/20 align-top">
              <td className="px-4 py-3"><div className="font-semibold">{n.org_name}</div><div className="text-xs text-[#63534E]">{n.contact_name} · {n.email}</div><div className="text-xs text-[#63534E] max-w-[260px] line-clamp-2">{n.mission}</div></td>
              <td className="px-4 py-3 text-xs uppercase">{n.registration_type}<div className="font-mono normal-case">{n.registration_number}</div>
                <button data-testid={`ngo-cert-${n.id}`} onClick={() => openPrivate(n.cert_path)} className="text-[#E65100] underline normal-case">View certificate</button></td>
              <td className="px-4 py-3 font-mono text-xs">{n.pan}</td>
              <td className="px-4 py-3 text-xs">{n.subscription ? `${n.subscription.tier_key.replace("_", " ")} · ${n.subscription.billing} · until ${n.subscription.active_until}` : "—"}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${pill(n.status)}`}>{n.status}</span></td>
              <td className="px-4 py-3"><div className="flex gap-2">
                {n.status !== "verified" && <Btn data-testid={`ngo-verify-${n.id}`} onClick={() => set(n.id, "verified")}>Verify</Btn>}
                {n.status !== "rejected" && <Btn data-testid={`ngo-reject-${n.id}`} className="bg-red-700 hover:bg-red-800" onClick={() => set(n.id, "rejected")}>Reject</Btn>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrantTable({ kind, endpoint, cols, row, testid }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (item, action) => {
    const body = { action };
    if (action === "approve") {
      const v = window.prompt("Amount to grant from the Seva Fund (USD)", String(item.amount_requested_usd ?? item.fees_needed_usd));
      if (!v) return; body.amount_usd = parseFloat(v);
    }
    if (action === "disburse") { body.reference = window.prompt("Payment reference (UTR / transaction ID)") || ""; }
    if (action === "reject") { body.note = window.prompt("Reason") || ""; }
    try { await api.put(`/admin/${endpoint}/${item.id}`, body); toast.success(`${kind} ${action}d`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid={testid} className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]">
        <Th cols={[...cols, "Granted", "Status", "Actions"]} />
        <tbody>
          {items.length === 0 && <tr><td colSpan={cols.length + 3} className="px-4 py-10 text-center text-[#63534E]">Nothing here yet.</td></tr>}
          {items.map((it) => (
            <tr key={it.id} className="border-t border-[#D4AF37]/20 align-top">
              {row(it)}
              <td className="px-4 py-3 font-mono">${it.amount_granted_usd}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${pill(it.status)}`}>{it.status}</span>{it.priority && <span className="ml-1 text-[10px] text-[#E65100]">PRIORITY</span>}</td>
              <td className="px-4 py-3"><div className="flex gap-2">
                {it.status === "pending" && <Btn data-testid={`${endpoint}-approve-${it.id}`} onClick={() => act(it, "approve")}>Approve</Btn>}
                {it.status === "pending" && <Btn data-testid={`${endpoint}-reject-${it.id}`} className="bg-red-700 hover:bg-red-800" onClick={() => act(it, "reject")}>Reject</Btn>}
                {it.status === "approved" && <Btn data-testid={`${endpoint}-disburse-${it.id}`} className="bg-[#E65100] hover:bg-[#BF360C]" onClick={() => act(it, "disburse")}>Mark disbursed</Btn>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const NgoProjectsAdminTab = () => (
  <GrantTable kind="Project" endpoint="ngo-projects" testid="ngo-projects-admin-tab" cols={["Project", "NGO", "Requested"]} row={(p) => (<>
    <td className="px-4 py-3"><div className="font-semibold">{p.title}</div><div className="text-xs text-[#63534E] max-w-[320px] line-clamp-2">{p.description}</div><div className="text-xs text-[#8C6D3B]">{p.category} · {p.beneficiaries} beneficiaries</div></td>
    <td className="px-4 py-3">{p.ngo_name}<div className="text-xs text-[#63534E]">{p.tier_key}</div></td>
    <td className="px-4 py-3 font-mono">${p.amount_requested_usd}</td></>)} />
);

export const ScholarshipsAdminTab = () => (
  <GrantTable kind="Scholarship" endpoint="scholarships" testid="scholarships-admin-tab" cols={["Student", "Applicant", "Fees", "Docs"]} row={(s) => (<>
    <td className="px-4 py-3"><div className="font-semibold">{s.student_name}</div><div className="text-xs text-[#63534E]">{s.course} · {s.institution} · {s.academic_year}</div><div className="text-xs text-[#63534E] max-w-[280px] line-clamp-2">{s.reason}</div></td>
    <td className="px-4 py-3">{s.applicant_name}<div className="text-xs text-[#63534E]">{s.applicant_email} · {s.relation}</div></td>
    <td className="px-4 py-3 font-mono">${s.fees_needed_usd}</td>
    <td className="px-4 py-3 text-xs"><button onClick={() => openPrivate(s.fee_doc_path)} className="text-[#E65100] underline block">Fee doc</button>{s.id_doc_path && <button onClick={() => openPrivate(s.id_doc_path)} className="text-[#E65100] underline">ID</button>}</td></>)} />
);

export function SevaFundAdminTab() {
  const [fund, setFund] = useState(null);
  const [pct, setPct] = useState("");
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    api.get("/admin/seva-fund").then((r) => { setFund(r.data); setPct(r.data.fee_share_percent); });
    api.get("/admin/ngo-tiers").then((r) => setTiers(r.data.items));
  }, []);
  const savePct = async () => {
    try { const { data } = await api.put("/admin/seva-fund", { fee_share_percent: parseFloat(pct) }); setFund(data); toast.success("Seva Fund share updated"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="seva-fund-admin-tab" className="space-y-6">
      {fund && (
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[["Platform fees", fund.platform_fees_total], ["Fund collected", fund.accrued], ["Granted", fund.granted], ["Paid out", fund.disbursed], ["Available", fund.available]].map(([l, v]) => (
              <div key={l}><div className="font-mono text-xl text-[#4A0E17]">${v}</div><div className="overline">{l}</div></div>
            ))}
          </div>
          <div className="mt-5 flex items-center gap-3">
            <span className="text-sm text-[#4A0E17]">Share of platform fees going to the Seva Fund</span>
            <Input data-testid="fund-pct" type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} className="w-24 h-9 bg-white" />%
            <Button data-testid="fund-pct-save" onClick={savePct} className="h-9 rounded-full bg-[#4A0E17]">Save</Button>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">{tiers.map((t) => <TierEditor key={t.key} tier={t} />)}</div>
    </div>
  );
}

function TierEditor({ tier }) {
  const [t, setT] = useState({ ...tier, perks: (tier.perks || []).join("\n"), max_requests_per_year: tier.max_requests_per_year ?? "" });
  const set = (k) => (e) => setT({ ...t, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      await api.put(`/admin/ngo-tiers/${tier.key}`, { name: t.name, yearly_usd: parseFloat(t.yearly_usd), monthly_usd: parseFloat(t.monthly_usd),
        max_requests_per_year: t.max_requests_per_year === "" ? null : parseInt(t.max_requests_per_year), max_amount_usd: parseFloat(t.max_amount_usd),
        featured: t.featured, priority: t.priority, logo_on_transparency: t.logo_on_transparency, perks: t.perks.split("\n").map((s) => s.trim()).filter(Boolean), active: t.active });
      toast.success(`${t.name} saved`);
    } catch (e) { toast.error(formatError(e)); }
  };
  const num = (k, l) => <label className="text-xs text-[#63534E]">{l}<Input data-testid={`tier-${tier.key}-${k}`} type="number" value={t[k]} onChange={set(k)} className="h-9 bg-white mt-1" /></label>;
  return (
    <div data-testid={`tier-editor-${tier.key}`} className="bg-[#FFFDF9] sacred-border rounded-2xl p-5 space-y-2">
      <Input value={t.name} onChange={set("name")} className="h-9 bg-white font-semibold" />
      <div className="grid grid-cols-2 gap-2">{num("yearly_usd", "Yearly $")}{num("monthly_usd", "Monthly $")}{num("max_amount_usd", "Max per project $")}{num("max_requests_per_year", "Requests/yr (blank = unlimited)")}</div>
      {[["featured", "Featured listing"], ["priority", "Priority review"], ["logo_on_transparency", "Logo on Transparency"], ["active", "Active"]].map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!t[k]} onChange={set(k)} /> {l}</label>
      ))}
      <textarea value={t.perks} onChange={set("perks")} rows={3} className="w-full text-xs border border-[#D4AF37]/40 rounded-md p-2" placeholder="Perks, one per line" />
      <Btn data-testid={`tier-save-${tier.key}`} className="bg-[#4A0E17] w-full" onClick={save}>Save tier</Btn>
    </div>
  );
}
